import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import CcpCpBreadcrumbs from "../../Layout/inc/CcpCpBreadcrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileImport,
  faFileDownload,
} from "@fortawesome/free-solid-svg-icons";
import useSchoolDropdown from "./useSchoolDropdown";

const initialForm = { highschool: "", bldg: "", pc: "", ops: "" };
const BLDG_OPTIONS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const CcpAdminBuildingMgt = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    highschool: "",
    bldg: "",
    pc: "",
    ops: "",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pathwayOptions, setPathwayOptions] = useState([]);
  const [operationsOptions, setOperationsOptions] = useState([]);

  const COLLECTION_NAME = "CcpBuildingMgt";
  const { data: highschools, loading: highschoolsLoading } =
    useSchoolDropdown();

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows = rows.sort((a, b) =>
        (a.highschool || "").localeCompare(b.highschool || ""),
      );
      setData(rows);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = data.filter(
      (row) =>
        (!filter.highschool ||
          (row.highschool || "")
            .toLowerCase()
            .includes(filter.highschool.toLowerCase())) &&
        (!filter.bldg ||
          (row.bldg || "").toLowerCase().includes(filter.bldg.toLowerCase())) &&
        (!filter.pc ||
          (row.pc || "").toLowerCase().includes(filter.pc.toLowerCase())) &&
        (!filter.ops ||
          (row.ops || "").toLowerCase().includes(filter.ops.toLowerCase())),
    );
    filtered = filtered.sort((a, b) =>
      (a.highschool || "").localeCompare(b.highschool || ""),
    );
    setFilteredData(filtered);
  }, [data, filter]);

  useEffect(() => {
    // Fetch coordinator options from Firestore (same as CcpAdminSchools)
    const fetchDropdownOptions = async () => {
      const { getFirestore, collection, query, where, orderBy, getDocs } =
        await import("firebase/firestore");
      const dbInstance = getFirestore();
      const pathwayQuery = query(
        collection(dbInstance, "users"),
        where("ccpAdminFlag", "==", true),
        orderBy("name"),
      );
      const operationsQuery = query(
        collection(dbInstance, "users"),
        where("ccpAdminFlag", "==", true),
        orderBy("name"),
      );
      try {
        const [pathwaySnapshot, operationsSnapshot] = await Promise.all([
          getDocs(pathwayQuery),
          getDocs(operationsQuery),
        ]);
        setPathwayOptions(pathwaySnapshot.docs.map((doc) => doc.data().name));
        setOperationsOptions(
          operationsSnapshot.docs.map((doc) => doc.data().name),
        );
      } catch (error) {
        console.error("Error fetching coordinator dropdown options:", error);
      }
    };
    fetchDropdownOptions();
  }, []);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex(
      (d) =>
        d.highschool === params.row.highschool && d.bldg === params.row.bldg,
    );
    setEditIndex(idx);
    setForm(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!form.highschool || !form.bldg) {
      setSnackbar({
        open: true,
        message: "All fields are required.",
        severity: "error",
      });
      return;
    }
    try {
      if (editIndex !== null) {
        const docId = data[editIndex].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), form);
        const newData = [...data];
        newData[editIndex] = { ...form, id: docId };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Building updated",
          severity: "success",
        });
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), form);
        setData([...data, { ...form, id: docRef.id }]);
        setSnackbar({
          open: true,
          message: "Building added",
          severity: "success",
        });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDeleteBuilding = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Building deleted",
        severity: "success",
      });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
    });
    const reader = new FileReader();

    const processExcelRow = async (row, merged, counts) => {
      const { added, updated } = counts;
      const idx = merged.findIndex(
        (d) => d.highschool === row.highschool && d.bldg === row.bldg,
      );
      if (idx > -1) {
        const docId = merged[idx].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), row);
        merged[idx] = { ...merged[idx], ...row };
        counts.updated = updated + 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), row);
        merged.push({ ...row, id: docRef.id });
        counts.added = added + 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const merged = [...data];
      const counts = { added: 0, updated: 0 };

      for (const row of jsonData) {
        await processExcelRow(row, merged, counts);
        setImportStatus((s) => ({
          ...s,
          total: jsonData.length,
          added: counts.added,
          updated: counts.updated,
        }));
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false }));
    };

    reader.readAsBinaryString(file);
  };

  const handleDeleteMultipleConfirmed = async () => {
    for (const id of rowSelectionModel) {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    }
    setData(data.filter((row) => !rowSelectionModel.includes(row.id)));
    setRowSelectionModel([]);
    setConfirmDeleteOpen(false);
    setSnackbar({
      open: true,
      message: "Selected buildings deleted",
      severity: "success",
    });
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  const handleDownloadTemplate = () => {
    const headers = ["highschool", "bldg", "pc", "ops"];
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "ccp-building-template.xlsx");
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
  }));

  const columns = [
    { field: "highschool", headerName: "Highschool", flex: 1, sortable: true },
    { field: "bldg", headerName: "Bldg", flex: 1, sortable: true },
    { field: "pc", headerName: "PC", flex: 1, sortable: true },
    { field: "ops", headerName: "Ops", flex: 1, sortable: true },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        CCP Building Management
      </Typography>
      <CcpCpBreadcrumbs />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="center"
        sx={{ mt: 3, mb: 3 }}
      >
        <TextField
          label="Filter Highschool"
          name="highschool"
          value={filter.highschool}
          onChange={(e) => setFilter({ ...filter, highschool: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Bldg"
          name="bldg"
          value={filter.bldg}
          onChange={(e) => setFilter({ ...filter, bldg: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter PC"
          name="pc"
          value={filter.pc}
          onChange={(e) => setFilter({ ...filter, pc: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Ops"
          name="ops"
          value={filter.ops}
          onChange={(e) => setFilter({ ...filter, ops: e.target.value })}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add
        </Button>
        <IconButton component="label" size="medium">
          <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </IconButton>
        <IconButton onClick={handleDownloadTemplate} size="medium">
          <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
        </IconButton>
        {rowSelectionModel && rowSelectionModel.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenConfirmDelete}
            sx={{ ml: 1 }}
          >
            Delete Selected
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          height: 500,
          width: "100%",
          background: "#fff",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onRowDoubleClick={handleOpenEdit}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel(newSelectionModel);
          }}
        />
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseModal}>
          {editIndex !== null ? "Edit Building" : "Add Building"}
        </ModalDialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Highschool</InputLabel>
              <Select
                label="Highschool"
                name="highschool"
                value={form.highschool}
                onChange={handleFormChange}
                disabled={highschoolsLoading}
              >
                {highschools.map((school) => (
                  <MenuItem key={school.school_name} value={school.school_name}>
                    {school.school_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Building as TextField, not dropdown */}
            <TextField
              label="Bldg"
              name="bldg"
              value={form.bldg}
              onChange={handleFormChange}
              required
              fullWidth
              sx={{ mb: 2 }}
            />
            {/* PC as dropdown */}
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>PC</InputLabel>
              <Select
                label="PC"
                name="pc"
                value={form.pc}
                onChange={handleFormChange}
              >
                {pathwayOptions.map((pc) => (
                  <MenuItem key={pc} value={pc}>
                    {pc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Ops as dropdown */}
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>Ops</InputLabel>
              <Select
                label="Ops"
                name="ops"
                value={form.ops}
                onChange={handleFormChange}
              >
                {operationsOptions.map((ops) => (
                  <MenuItem key={ops} value={ops}>
                    {ops}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={editIndex !== null ? handleDeleteBuilding : undefined}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteMultipleConfirmed}
        title="Delete Selected Buildings"
        message={`Are you sure you want to delete ${rowSelectionModel.length} selected building${
          rowSelectionModel.length === 1 ? "" : "s"
        }? This action cannot be undone.`}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
      {importStatus.open && (
        <Box sx={{ mt: 2 }}>
          {importStatus.loading && <LinearProgress />}
          <Alert severity="info" sx={{ mt: 1 }}>
            Importing Excel:{" "}
            {importStatus.loading ? "Processing..." : "Complete."} <br />
            Added: {importStatus.added} &nbsp; Updated: {importStatus.updated}{" "}
            &nbsp; Total: {importStatus.total}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default CcpAdminBuildingMgt;
